import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { timeAPI, teamsAPI, settingsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import { Clock, DollarSign, FileText, Filter, Calendar as CalendarIcon, Download } from 'lucide-react';

const TimeReportPage = () => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [dateRange, setDateRange] = useState('this_week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [teamFilter, setTeamFilter] = useState('');

  const getDateRange = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case 'today':
        return { start: format(now, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'this_week':
        return { start: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
      case 'last_week': {
        const lw = subWeeks(now, 1);
        return { start: format(startOfWeek(lw, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(lw, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
      }
      case 'this_month':
        return { start: format(startOfMonth(now), 'yyyy-MM-dd'), end: format(endOfMonth(now), 'yyyy-MM-dd') };
      case 'last_month': {
        const lm = subMonths(now, 1);
        return { start: format(startOfMonth(lm), 'yyyy-MM-dd'), end: format(endOfMonth(lm), 'yyyy-MM-dd') };
      }
      case 'custom':
        return { start: customStart, end: customEnd };
      default:
        return {};
    }
  }, [dateRange, customStart, customEnd]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (teamFilter && teamFilter !== 'all') params.team_id = teamFilter;
      const res = await timeAPI.getReport(params);
      setReport(res.data);
    } catch (error) {
      toast.error('Failed to load time report');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, teamFilter]);

  useEffect(() => {
    teamsAPI.list().then(res => setTeams(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (dateRange !== 'custom' || (customStart && customEnd)) {
      fetchReport();
    }
  }, [dateRange, teamFilter, fetchReport]);

  // Group entries by task
  const groupedByTask = {};
  if (report?.entries) {
    for (const entry of report.entries) {
      if (!groupedByTask[entry.task_id]) {
        groupedByTask[entry.task_id] = { title: entry.task_title, entries: [], total: 0 };
      }
      groupedByTask[entry.task_id].entries.push(entry);
      groupedByTask[entry.task_id].total += entry.duration_minutes || 0;
    }
  }

  // Group entries by day
  const groupedByDay = {};
  if (report?.entries) {
    for (const entry of report.entries) {
      const day = format(new Date(entry.check_in), 'yyyy-MM-dd');
      if (!groupedByDay[day]) {
        groupedByDay[day] = { entries: [], total: 0 };
      }
      groupedByDay[day].entries.push(entry);
      groupedByDay[day].total += entry.duration_minutes || 0;
    }
  }

  const formatDuration = (mins) => {
    if (!mins) return '0m';
    if (mins >= 60) return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
    return `${Math.round(mins)}m`;
  };

  const handleExport = () => {
    if (!report?.entries?.length) return;
    const { start, end } = getDateRange();
    const rows = [['Date', 'Task', 'Duration (min)', 'Duration (hrs)', 'Note', 'Type']];
    for (const entry of report.entries) {
      rows.push([
        format(new Date(entry.check_in), 'yyyy-MM-dd'),
        entry.task_title,
        Math.round(entry.duration_minutes || 0),
        ((entry.duration_minutes || 0) / 60).toFixed(2),
        entry.note || '',
        entry.is_manual ? 'Manual' : entry.auto_closed ? 'Auto-closed' : 'Timer',
      ]);
    }
    rows.push([]);
    rows.push(['Total Hours', '', '', report.total_hours]);
    rows.push(['Hourly Rate (AUD)', '', '', `$${report.hourly_rate}`]);
    rows.push(['Total Amount (AUD)', '', '', `$${report.total_amount}`]);

    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-${start || 'all'}-to-${end || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  return (
    <div className="space-y-6" data-testid="time-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading">Time Reports</h1>
          <p className="text-slate-600">Track and bill your work time in AUD</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={!report?.entries?.length} data-testid="export-time-report-btn">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs text-slate-500">Date Range</Label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40 mt-1" data-testid="date-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="last_week">Last Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dateRange === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="mt-1 w-40" data-testid="custom-start-date" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="mt-1 w-40" data-testid="custom-end-date" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs text-slate-500">Team</Label>
              <Select value={teamFilter} onValueChange={setTeamFilter}>
                <SelectTrigger className="w-40 mt-1" data-testid="team-filter-select">
                  <SelectValue placeholder="All Teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.group_id} value={t.group_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Hours</p>
                <p className="text-xl font-bold" data-testid="total-hours">{report.total_hours}h</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Rate</p>
                <p className="text-xl font-bold" data-testid="hourly-rate">${report.hourly_rate}/hr</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Amount (AUD)</p>
                <p className="text-xl font-bold" data-testid="total-amount">${report.total_amount}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : report?.entries?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600 font-medium">No time entries for this period</p>
            <p className="text-sm text-slate-400 mt-1">Check in to a task to start tracking time</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* By Task */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-heading">By Task</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {Object.entries(groupedByTask).map(([taskId, data]) => (
                  <div key={taskId} className="px-4 py-3 flex items-center justify-between" data-testid={`task-row-${taskId}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{data.title}</p>
                      <p className="text-xs text-slate-500">{data.entries.length} {data.entries.length === 1 ? 'entry' : 'entries'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-medium text-sm">{formatDuration(data.total)}</p>
                      {report.hourly_rate > 0 && (
                        <p className="text-xs text-green-600">${((data.total / 60) * report.hourly_rate).toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* By Day */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-heading">By Day</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {Object.entries(groupedByDay).sort(([a], [b]) => b.localeCompare(a)).map(([day, data]) => (
                  <div key={day} className="px-4 py-3" data-testid={`day-row-${day}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm">{format(new Date(day + 'T00:00:00'), 'EEEE, MMM d')}</p>
                      <p className="font-mono font-medium text-sm">{formatDuration(data.total)}</p>
                    </div>
                    <div className="space-y-1">
                      {data.entries.map((entry) => (
                        <div key={entry.entry_id} className="flex items-center justify-between text-xs text-slate-500 pl-2">
                          <span className="truncate flex-1">{entry.task_title}{entry.note ? ` — ${entry.note}` : ''}</span>
                          <div className="flex items-center gap-2 ml-2">
                            {entry.is_manual && <Badge variant="outline" className="text-[10px] h-4">Manual</Badge>}
                            <span className="font-mono">{formatDuration(entry.duration_minutes)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Billing Summary */}
          {report.hourly_rate > 0 && (
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <h3 className="font-heading font-bold text-base mb-3">Billing Summary</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Total Time</p>
                    <p className="font-bold">{report.total_hours} hours</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Rate</p>
                    <p className="font-bold">${report.hourly_rate} AUD/hr</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Amount</p>
                    <p className="font-bold text-green-700">${report.total_amount} AUD</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Entries</p>
                    <p className="font-bold">{report.entries.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeReportPage;
